import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useAppDispatch from "hooks/useAppDispatch";
import LappaLogo from "img/logos/lappa-logo.svg";
import { findInviteByToken } from "utils/invites.js";
import eyeOpenImg from "img/icons/misc/eye-on.svg";
import eyeCloseImg from "img/icons/misc/eye-off.svg";
import errorIcon from "img/icons/status/alert-circle.png";
import {
  validateInvitationToken,
  clearValidateError,
  signupFromInvitation,
} from "../../../../redux/features/invitation/invitationSlice";
import InvitationInvalidModal from "../../../Authentication/screens/InvitationInvalidModal/InvitationInvalidModal";
import "./InviteProfileSetup.sass";

export default function InviteProfileSetup() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const token = searchParams.get("token");

  const validateStatus = useSelector(
    (state) => state.invitation?.validateStatus || "idle"
  );
  const validateError = useSelector(
    (state) => state.invitation?.validateError || null
  );
  const validateData = useSelector(
    (state) => state.invitation?.validateData || null
  );
  const signupStatus = useSelector(
    (state) => state.invitation?.signupStatus || "idle"
  );
  const signupError = useSelector(
    (state) => state.invitation?.signupError || null
  );

  const [invite, setInvite] = useState(null);
  const lastValidatedTokenRef = useRef(null);

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    phone: "",
  });

  const [touched, setTouched] = useState({});
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!token) {
      lastValidatedTokenRef.current = null;
      dispatch(clearValidateError());
      setInvite(null);
      return;
    }

    if (lastValidatedTokenRef.current === token) {
      return;
    }

    if (validateData && validateData.token === token) {
      lastValidatedTokenRef.current = token;
      return;
    }

    dispatch(clearValidateError());
    lastValidatedTokenRef.current = token;
    dispatch(validateInvitationToken(token));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) {
      setInvite(null);
      return;
    }

    if (validateStatus === "succeeded" && validateData && validateData.token === token) {
      setInvite({
        email: validateData.email,
        inviterName: validateData.inviter_name,
        companyName: validateData.company_name,
        message: validateData.message,
        joiningAs: validateData.joining_as,
      });
      return;
    }

    if (validateStatus === "idle" && !validateData && !invite) {
      const found = findInviteByToken(token);
      if (found) {
        setInvite(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateStatus, validateData, token]);

  useEffect(() => {
    if (signupStatus === "succeeded") {
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signupStatus]);

  const email = invite?.email || "";

  const errors = useMemo(() => {
    const e = {};

    if (!values.firstName.trim()) e.firstName = "First name is required";

    if (!values.lastName.trim()) e.lastName = "Last name is required";

    if (!values.password) e.password = "Password is required";
    else if (values.password.length < 8) e.password = "At least 8 characters";

    if (!values.confirmPassword) e.confirmPassword = "Please confirm password";
    else if (values.confirmPassword !== values.password)
      e.confirmPassword = "Passwords do not match";
    return e;
  }, [values]);

  const isFormValid = useMemo(() => {
    return (
      values.firstName.trim() &&
      values.lastName.trim() &&
      values.password &&
      values.password.length >= 8 &&
      values.confirmPassword &&
      values.confirmPassword === values.password &&
      Object.keys(errors).length === 0
    );
  }, [values, errors]);

  const showError = (name) => (touched[name] ? Boolean(errors[name]) : false);
  const getError = (name) => (showError(name) ? errors[name] : "");

  const onChange = (name) => (e) => {
    setValues((p) => ({ ...p, [name]: e.target.value }));
  };

  const onBlur = (name) => () => setTouched((p) => ({ ...p, [name]: true }));

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("Image size should be less than 5MB");
      return;
    }

    setAvatarFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleChangeAvatar = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveAvatar = () => {
    setAvatarUrl(null);
    setAvatarFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    setTouched({
      firstName: true,
      lastName: true,
      password: true,
      confirmPassword: true,
      phone: true,
    });

    if (Object.keys(errors).length > 0) return;

    try {
      await dispatch(
        signupFromInvitation({
          token,
          firstName: values.firstName,
          lastName: values.lastName,
          password: values.password,
          phone: values.phone,
          avatarFile,
        })
      );
    } catch (err) {
      
    }
  };

  const handleLogIn = () => navigate("/login");

  const handleCancel = () => {
    navigate("/login", { replace: true });
  };

  const handleJoin = () => {
    navigate("/login", { replace: true });
  };


  if (validateStatus === "loading" && !invite) {
    return (
      <div className="InviteProfileSetup">
        <header className="InviteProfileSetup__header">
          <img className="InviteProfileSetup__logo" src={LappaLogo} alt="LAPPA" />
        </header>
        <main className="InviteProfileSetup__main">
          <p>Checking invitation…</p>
        </main>
      </div>
    );
  }

  const showErrorModal =
    (!token && validateStatus !== "loading") ||
    (validateStatus === "failed" && !invite) ||
    (signupStatus === "failed");

  const errorMessage = !token
    ? "Invalid invitation link."
    : signupError?.message ||
      validateError?.message ||
      "This invitation is invalid or has expired. Please request a new invitation.";

  if (!invite && validateStatus !== "loading") {
    return (
      <div className="InviteProfileSetup">
        <header className="InviteProfileSetup__header">
          <img className="InviteProfileSetup__logo" src={LappaLogo} alt="LAPPA" />
        </header>
        <main className="InviteProfileSetup__main">
          {validateStatus === "failed" ? (
            <>
              <h1 className="InviteProfileSetup__title">{t("auth.invite.invalidInvite")}</h1>
              <p className="InviteProfileSetup__subTitle">{errorMessage}</p>
            </>
          ) : (
            <p>Loading…</p>
          )}
        </main>
        <InvitationInvalidModal
          open={showErrorModal}
          title={t("auth.invite.joinAccountTitle")}
          description={errorMessage}
          onCancel={handleCancel}
          onJoin={handleJoin}
          cancelText={t("auth.invite.cancel")}
          joinText={t("auth.invite.joinAccount")}
        />
      </div>
    );
  }

  return (
    <div className="InviteProfileSetup">
      <header className="InviteProfileSetup__header">
        <img className="InviteProfileSetup__logo" src={LappaLogo} alt="LAPPA" />
      </header>

      <main className="InviteProfileSetup__main">
        <h1 className="InviteProfileSetup__title">Let’s set up your profile</h1>
        <p className="InviteProfileSetup__subTitle">This will only take a minute</p>

        <div className="InviteProfileSetup__avatarSection">
          <div className="InviteProfileSetup__avatarWrap">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Profile"
                className="InviteProfileSetup__avatarImage"
                onClick={handleAvatarClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleAvatarClick();
                  }
                }}
              />
            ) : (
              <div
                className="InviteProfileSetup__avatarPlaceholder"
                onClick={handleAvatarClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleAvatarClick();
                  }
                }}
                aria-label="Upload profile picture"
              />
            )}
          </div>
          {avatarUrl && (
            <div className="InviteProfileSetup__avatarActions">
              <button
                type="button"
                className="InviteProfileSetup__avatarBtn InviteProfileSetup__avatarBtn--change"
                onClick={handleChangeAvatar}
              >
                Change
              </button>

              <button
                type="button"
                className="InviteProfileSetup__avatarBtn InviteProfileSetup__avatarBtn--remove"
                onClick={handleRemoveAvatar}
              >
                Remove
              </button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </div>

        <div className="InviteProfileSetup__form">
          <Field
            label="What's your first name?"
            required
            value={values.firstName}
            placeholder="John"
            onChange={onChange("firstName")}
            onBlur={onBlur("firstName")}
            error={getError("firstName")}
          />

          <div className="InviteProfileSetup__gap12" />

          <Field
            label="What's your last name?"
            required
            value={values.lastName}
            placeholder="Smith"
            onChange={onChange("lastName")}
            onBlur={onBlur("lastName")}
            error={getError("lastName")}
          />

          <div className="InviteProfileSetup__gap12" />

          <Field
            label="Create password"
            required
            value={values.password}
            placeholder="at least 8 characters"
            type={showPwd ? "text" : "password"}
            onChange={onChange("password")}
            onBlur={onBlur("password")}
            error={getError("password")}
            rightButton={
              <button
                type="button"
                className="InviteProfileSetup__rightBtn"
                onClick={() => setShowPwd((s) => !s)}
                aria-label={showPwd ? "Hide password" : "Show password"}
              >
                <img
                  className="InviteProfileSetup__rightIcon"
                  src={showPwd ? eyeOpenImg : eyeCloseImg}
                  alt=""
                  aria-hidden="true"
                />
              </button>
            }
          />

          <div className="InviteProfileSetup__gap12" />

          <Field
            label="Confirm new password"
            required
            value={values.confirmPassword}
            placeholder="at least 8 characters"
            type={showPwd2 ? "text" : "password"}
            onChange={onChange("confirmPassword")}
            onBlur={onBlur("confirmPassword")}
            error={getError("confirmPassword")}
            rightButton={
              <button
                type="button"
                className="InviteProfileSetup__rightBtn"
                onClick={() => setShowPwd2((s) => !s)}
                aria-label={showPwd2 ? "Hide password" : "Show password"}
              >
                <img
                  className="InviteProfileSetup__rightIcon"
                  src={showPwd2 ? eyeOpenImg : eyeCloseImg}
                  alt=""
                  aria-hidden="true"
                />
              </button>
            }
          />

          <div className="InviteProfileSetup__gap12" />

          <Field
            label={
              <>
                Phone number <span className="InviteProfileSetup__optional">(Optional)</span>
              </>
            }
            value={values.phone}
            placeholder="+__ ___-___-__-__"
            onChange={onChange("phone")}
            onBlur={onBlur("phone")}
            error={""}
          />
        </div>

        <div className="InviteProfileSetup__joiningWrap">
          <p className="InviteProfileSetup__joining">
            You’re joining as{" "}
            <span className="InviteProfileSetup__joiningEmail">{email}</span>
          </p>

          <button
            className="InviteProfileSetup__wrong"
            type="button"
            onClick={handleLogIn}
          >
            {t("auth.invite.wrongAccountLogin")} <span className="InviteProfileSetup__link">{t("auth.invite.logInInstead")}</span> {t("auth.invite.instead")}
          </button>
        </div>

        <button
          className="InviteProfileSetup__cta"
          type="button"
          onClick={handleSubmit}
          disabled={!isFormValid || signupStatus === "loading"}
        >
          {signupStatus === "loading" ? t("auth.invite.processing") : t("auth.invite.continue")}
        </button>
      </main>
    </div>
  );
}

function Field({
  label,
  required,
  value,
  placeholder,
  type = "text",
  onChange,
  onBlur,
  error,
  rightButton,
}) {
  const hasError = Boolean(error);

  return (
    <div className={`InviteProfileSetup__field ${hasError ? "InviteProfileSetup__field--error" : ""}`}>
      <div className="InviteProfileSetup__label">
        {label} {required ? <span className="InviteProfileSetup__required">*</span> : null}
      </div>

      <div className="InviteProfileSetup__controlWrap">
        <input
          className="InviteProfileSetup__input"
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          aria-invalid={hasError}
        />

        {rightButton ? rightButton : hasError ? (
          <div className="InviteProfileSetup__rightIconBox" aria-hidden="true">
            <img className="InviteProfileSetup__rightIcon" src={errorIcon} alt="" />
          </div>
        ) : null}
      </div>

      <div className="InviteProfileSetup__errorText">
        {hasError ? error : "\u00A0"}
      </div>
    </div>
  );
}
